/* Only native tariff variants are selected here. APO owns option pricing. */
(() => {
  const source = document.getElementById('italflex-bed-shipping-products');
  if (!source) return;
  let data;
  try { data = JSON.parse(source.textContent); } catch { return; }
  const normal = text => String(text || '').normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '').trim();
  const dimension = text => {
    const found = normal(text).match(/^(90|120|140|160|180)\s*[x×]\s*200\b/i);
    return found ? `${found[1]}x200` : null;
  };
  const products = [...new Map(data.products.filter(product =>
    product?.handle?.startsWith('pat-tapi') && data.activeHandles?.includes(product.handle) &&
    product.options?.some(option => normal(option.name || option) === 'Configuratie')
  ).map(product => [String(product.id), product])).values()];
  if (!products.length) return;
  const byVariant = new Map(products.flatMap(product =>
    product.variants.map(variant => [String(variant.id), product])));
  const modelFor = product => data.unification?.models?.[product?.handle];
  function isUnified(product) {
    const model = modelFor(product);
    if (!model || String(product.id) !== String(model.productId) || product.variants.length !== 2 ||
        new Set(product.variants.map(variant => String(variant.id))).size !== 2) return false;
    const names = product.options.map(option => normal(option.name || option));
    const configIndex = names.indexOf('Configuratie'), sizeIndex = names.indexOf('Dimensiune');
    if (configIndex < 0 || new Set(names).size !== names.length ||
        names.some(name => !['Configuratie', 'Dimensiune'].includes(name))) return false;
    // Only the exact kept pair is dimension-independent. During migration the
    // existing native Dimensiune field may remain with one value; keep that
    // metadata intact while APO continues to own its real selectable sizes.
    const pair = Object.entries(model.legacyVariants || {}).filter(([id, entry]) => id === String(entry.targetVariantId));
    if (pair.length !== 2 || new Set(pair.map(([, entry]) => entry.configuration)).size !== 2 ||
        !product.variants.every(variant => pair.some(([id, entry]) =>
          id === String(variant.id) && entry.configuration === variant.options[configIndex]))) return false;
    if (sizeIndex < 0) return names.length === 1;
    const sizes = product.variants.map(variant => dimension(variant.options[sizeIndex]));
    return names.length === 2 && !!sizes[0] && sizes.every(size => size === sizes[0]) &&
      pair.every(([, entry]) => dimension(entry.dimension) === sizes[0]);
  }
  // Historical IDs are lookup-only. No deleted variant is recreated by this
  // code. Shopify may discard unavailable old cart lines before we can read it.
  products.filter(isUnified).forEach(product => {
    Object.keys(modelFor(product).legacyVariants || {}).forEach(id => byVariant.set(String(id), product));
  });
  function referenceFor(product, size) {
    const references = modelFor(product)?.compareAtByDimensionCents;
    return references && Object.hasOwn(references, size) ? references[size] : null;
  }
  function existingVisibleSize(product, size) {
    // Tulip's90/120/140 widths follow the merchant-confirmed commercial list.
    // APO retains every existing surcharge and compatibility condition.
    return product?.handle !== 'pat-tapitat-tulip' || size === '90x200' || size === '120x200' || size === '140x200';
  }
  function requestedDimension(product, variantId, explicitSize) {
    const model = modelFor(product);
    if (!model || !isUnified(product)) return null;
    if (dimension(explicitSize) && Object.hasOwn(model.compareAtByDimensionCents, dimension(explicitSize)))
      return existingVisibleSize(product, dimension(explicitSize)) ? dimension(explicitSize) : null;
    const legacySize = dimension(model.legacyVariants?.[String(variantId)]?.dimension);
    return existingVisibleSize(product, legacySize) ? legacySize : null;
  }
  function moneyCents(text) {
    const value = String(text || '').replace(/[^\d.,-]/g, '');
    if (!value) return null;
    const decimal = value.match(/[.,](\d{2})$/);
    const parsed = decimal ? Number(value.slice(0, -3).replace(/[.,]/g, '') + '.' + decimal[1]) :
      Number(value.replace(/[.,]/g, ''));
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
  }
  const moneyText = cents => new Intl.NumberFormat(document.documentElement.lang || 'ro-RO', {
    style: 'currency', currency: data.currency || 'RON'
  }).format(cents / 100);
  function productReference(reference, sale) {
    return Number.isFinite(reference) && Number.isFinite(sale) && reference > sale ? reference : null;
  }
  function cartReference(reference, original, final) {
    // Preserve real discounts independently of historical compare-at values.
    return final < original ? Math.max(reference || 0, original) : productReference(reference, original);
  }
  function updatePriceLabels(container, compare, show, cart) {
    const price = container.querySelector(cart ? '[data-hulkapps-ci-price]:not(.compare-at-price)' : '.price');
    if (!price) return;
    const group = cart ? null : price.closest('[role="group"]');
    let label = price.querySelector('.visually-hidden') || group?.querySelector('.visually-hidden') || price.previousElementSibling;
    if (!label?.classList?.contains('visually-hidden')) {
      label = document.createElement('span'); label.className = 'visually-hidden';
      price.before(label);
    }
    const title = show ? 'Preț la ofertă ' : 'Preț ';
    if (label.textContent !== title) label.textContent = title;
    if (label.hidden) label.hidden = false;
    // Some app-rendered groups add an explicit name in addition to Horizon's
    // visually-hidden label. Keep both names aligned with the actual display.
    if (group?.hasAttribute('aria-label')) {
      const name = title + (price.querySelector('bdi') || price).textContent.trim();
      if (group.getAttribute('aria-label') !== name) group.setAttribute('aria-label', name);
    }
    if (cart) {
      let regular = compare?.previousElementSibling;
      if (compare && !regular?.classList?.contains('visually-hidden')) {
        regular = document.createElement('span'); regular.className = 'visually-hidden';
        regular.textContent = 'Preț obișnuit '; compare.before(regular);
      }
      if (regular && regular !== label && regular.hidden === show) regular.hidden = !show;
    }
  }
  function renderCompare(container, reference, sale, cart = false) {
    let node = container.querySelector('.compare-at-price');
    const show = Number.isFinite(reference) && reference > sale;
    if (!node && !show) { updatePriceLabels(container, null, false, cart); return; }
    if (!node) {
      node = document.createElement(cart ? 's' : 'span');
      node.className = 'compare-at-price';
      node.append(document.createElement('bdi'));
      if (cart) container.append(node);
      else {
        const group = document.createElement('span'); group.setAttribute('role', 'group');
        const label = document.createElement('span'); label.className = 'visually-hidden';
        label.textContent = 'Preț obișnuit '; group.append(label, node); container.prepend(group);
      }
    }
    const wrapper = cart ? node : node.closest('[role="group"]') || node;
    if (wrapper.hidden === show) wrapper.hidden = !show;
    // Horizon 4 nests its amount and accessible label inside the price group.
    const amount = node.querySelector('bdi') || node;
    if (show && amount.textContent !== moneyText(reference)) amount.textContent = moneyText(reference);
    updatePriceLabels(container, node, show, cart);
  }
  function updateProductReferences(product, size) {
    if (!isUnified(product) || String(product.id) !== String(data.current)) return;
    const reference = referenceFor(product, size);
    document.querySelectorAll(`product-price[data-product-id="${product.id}"] [ref="priceContainer"], sticky-add-to-cart [ref="priceContainer"]`).forEach(container => {
      const sale = moneyCents(container.querySelector('.price')?.textContent);
      if (sale !== null) renderCompare(container, productReference(reference, sale), sale);
    });
  }
  // Use the same metafield as buy-buttons' hidden properties[Model]. APO may
  // omit this non-editable field; never infer a label from a handle or cart cache.
  function cartModelProduct(item) {
    const product = byVariant.get(String(item.variant_id || item.id));
    return isUnified(product) && (!item.product_id || String(item.product_id) === String(product.id)) ? product : null;
  }
  function restoreModelProperty(item) {
    const product = cartModelProduct(item);
    if (!product || !item.properties) return item.properties;
    const entries = Array.isArray(data.modelLabels) ? data.modelLabels.filter(entry =>
      entry && String(entry.productId) === String(product.id)) : [];
    const label = entries[0]?.label;
    const canonical = typeof label === 'string' && label.trim() && entries.every(entry => entry.label === label);
    const properties = !Object.hasOwn(item.properties, 'Model') && canonical ? {...item.properties, Model: label} : item.properties;
    // Known kept-pair projection bridge. Treat Mageworx's price as an opaque
    // string: append Model to its known attribute schema, never calculate it.
    if (!product.variants.some(variant => String(variant.id) === String(item.variant_id || item.id)) ||
        !Object.hasOwn(item.properties, '__mws_apo_cta')) return properties;
    if (!canonical || properties.Model !== label) return item.properties;
    const raw = item.properties.__mws_apo_cta;
    if (typeof raw !== 'string') return item.properties;
    let projection;
    try { projection = JSON.parse(raw); } catch { return item.properties; }
    if (!projection || Array.isArray(projection) || Object.keys(projection).sort().join(',') !== 'attributes,price' ||
        typeof projection.price !== 'string' || !Array.isArray(projection.attributes) ||
        projection.attributes.some(entry => !entry || Array.isArray(entry) || typeof entry !== 'object' ||
          Object.keys(entry).sort().join(',') !== 'key,value' || typeof entry.key !== 'string' || typeof entry.value !== 'string') ||
        JSON.stringify(projection) !== raw) return item.properties;
    if (new Set(projection.attributes.map(entry => entry.key)).size !== projection.attributes.length ||
        projection.attributes.some(entry => entry.key !== 'Model' &&
          (!Object.hasOwn(item.properties, entry.key) || item.properties[entry.key] !== entry.value))) return item.properties;
    const existing = projection.attributes.filter(entry => entry.key === 'Model');
    if (existing.length > 1 || existing.some(entry => entry.value !== label)) return item.properties;
    if (existing.length) return properties;
    return {...properties, __mws_apo_cta: JSON.stringify({...projection,
      attributes: [...projection.attributes, {key: 'Model', value: label}]})};
  }
  let currentCart = data.cart, referenceCartRequest, unknownCartRows = '', cartReferenceRevision = 0;
  const cartItems = cart => Array.isArray(cart?.items) ? cart.items : [];
  const positiveCartId = value => (typeof value === 'string' && /^[1-9]\d*$/.test(value)) ||
    (Number.isSafeInteger(value) && value > 0);
  const validCartItem = item => item && typeof item === 'object' && !Array.isArray(item) &&
    typeof item.key === 'string' && !!item.key.trim() && Number.isSafeInteger(item.quantity) && item.quantity >= 1 &&
    positiveCartId(item.product_id) && positiveCartId(item.variant_id ?? item.id) &&
    Number.isFinite(item.original_price) && Number.isFinite(item.final_price) &&
    (item.properties == null || (typeof item.properties === 'object' && !Array.isArray(item.properties)));
  function refreshCartReferenceData() {
    if (referenceCartRequest) return referenceCartRequest;
    const revision = cartReferenceRevision;
    referenceCartRequest = cartRequest('cart.js').then(cart => {
      // A late response must not replace a newer cart/discount event resource.
      if (revision !== cartReferenceRevision) return;
      if (!Array.isArray(cart?.items) || !cart.items.every(validCartItem))
        throw new Error('Răspuns de coș invalid.');
      currentCart = cart; unknownCartRows = ''; updateCartReferences();
    }).catch(error => {
      unknownCartRows = '';
      console.warn('ItalFlex reference prices:', error);
    }).finally(() => {
      referenceCartRequest = undefined;
      // An event without a full resource may have invalidated this request.
      // Fetch once more for that newer revision, but never loop on failures.
      if (revision !== cartReferenceRevision && !currentCart) refreshCartReferenceData();
    });
    return referenceCartRequest;
  }
  function cartResourceChanged(event) {
    const resource = event.detail?.resource;
    const fullCart = Number.isInteger(resource?.item_count) && Array.isArray(resource?.items) &&
      resource.items.every(validCartItem);
    cartReferenceRevision++; unknownCartRows = '';
    if (fullCart) {
      currentCart = resource; updateCartReferences();
    } else {
      // CartAddEvent can carry only added items. Never treat that as a whole
      // cart or redraw same-key prices from the previous cart snapshot.
      currentCart = null; refreshCartReferenceData();
    }
    schedule();
  }
  function updateCartReferences() {
    const rows = [...document.querySelectorAll('.cart-items__table-row[data-key]')];
    for (const row of rows) {
      const item = cartItems(currentCart).find(item => item?.key === row.dataset.key);
      const product = item && byVariant.get(String(item.variant_id || item.id));
      if (!isUnified(product) || !item.properties?.Dimensiune) continue;
      const container = row.querySelector('.cart-items__unit-price-wrapper > div');
      if (!container) continue;
      const reference = referenceFor(product, dimension(item.properties.Dimensiune));
      renderCompare(container, cartReference(reference, item.original_price, item.final_price), item.final_price, true);
    }
    const unknown = rows.map(row => row.dataset.key).filter(key => !cartItems(currentCart).some(item => item?.key === key)).sort().join('|');
    if (!unknown || unknown === unknownCartRows || referenceCartRequest) return;
    unknownCartRows = unknown;
    refreshCartReferenceData();
  }
  const pageProduct = products.find(product => String(product.id) === String(data.current));
  const pageQuery = new URLSearchParams(location.search || '');
  const requestedSize = requestedDimension(pageProduct, pageQuery.get('variant'), pageQuery.get('if.size'));
  let restoreSizePending = !!requestedSize;
  function restoreLinkedSize(root, product) {
    if (!restoreSizePending || product !== pageProduct || !existingVisibleSize(product, requestedSize)) return false;
    const option = [...root.querySelectorAll('.mw-option')].find(option => optionName(option) === 'Dimensiune');
    const input = [...option?.querySelectorAll('input[type="radio"]') || []].find(input =>
      dimension(input.getAttribute('aria-label')) === requestedSize && !input.disabled &&
      input.closest('.mw-option__value')?.getClientRects().length);
    if (!input) return false;
    restoreSizePending = false;
    if (input.checked) return false;
    input.click(); return true;
  }
  function rememberLinkedSize(product, size, variant) {
    if (!isUnified(product) || product !== pageProduct || !size || !existingVisibleSize(product, size) || restoreSizePending || !location.pathname.startsWith('/products/')) return;
    const url = new URL(location.href);
    url.searchParams.set('variant', String(variant.id)); url.searchParams.set('if.size', size);
    if (url.href !== location.href) history.replaceState(history.state, '', url);
  }
  function tariffVariant(product, mattress, size) {
    const configIndex = product.options.findIndex(option => normal(option.name || option) === 'Configuratie');
    const sizeIndex = product.options.findIndex(option => normal(option.name || option) === 'Dimensiune');
    const config = normal(mattress) === 'Fara saltea' ? 'Fără saltea' : 'Cu saltea';
    return product.variants.find(item => item.options[configIndex] === config &&
      (isUnified(product) || sizeIndex < 0 || !size || dimension(item.options[sizeIndex]) === size));
  }
  // APO edits prices/properties in the cart but retains the original native ID.
  // Replace a mismatching bed variant, retaining every APO property and qty.
  // A missing saved Model alone needs only an in-place properties update.
  const cartRoot = window.Shopify?.routes?.root || '/';
  let reconciling, removalRequest, cartEditOpening, cartEditReplay, cartEditPending = false, reconciliationFailed = false;
  function mismatches(cart) {
    return cartItems(cart).flatMap(item => {
      if (!item || typeof item !== 'object') return [];
      const product = byVariant.get(String(item.variant_id || item.id));
      if (!product || !item.properties?.Saltea) return [];
      const variant = tariffVariant(product, item.properties.Saltea, dimension(item.properties.Dimensiune));
      const properties = restoreModelProperty(item);
      return variant && (String(variant.id) !== String(item.variant_id || item.id) || properties !== item.properties) ? [{item, variant, properties}] : [];
    });
  }
  async function cartRequest(path, payload) {
    const response = await fetch(cartRoot + path, payload ? {
      method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(payload)
    } : {cache: 'no-store'});
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || 'Actualizarea coșului nu a reușit.');
    return result;
  }
  const inconsistentBedQuantity = cart => cart.items.some(item => cartModelProduct(item) &&
    Object.hasOwn(item.properties || {}, '_mws_qty') && item.properties._mws_qty !== String(item.quantity));
  async function reconcileCart(cart) {
    if (removalRequest || cartEditOpening) return true;
    if (reconciling) return reconciling;
    reconciling = (async () => {
      let freshCart = cart;
      if (!freshCart) {
        let invalidatedReads = 0;
        while (true) {
          const revision = cartReferenceRevision;
          freshCart = await cartRequest('cart.js');
          if (revision === cartReferenceRevision) break;
          if (++invalidatedReads === 3) throw new Error('Coșul s-a modificat în timpul verificării. Reîncărcați coșul.');
        }
      }
      if (!Array.isArray(freshCart?.items) || !freshCart.items.every(validCartItem))
        throw new Error('Răspuns de coș invalid.');
      if (cartEditPending) throw new Error('Editarea opțiunilor este în curs. Reîncărcați și verificați produsul.');
      if (inconsistentBedQuantity(freshCart)) throw new Error('Cantitatea produsului necesită verificare și reconfigurare.');
      if (nativeMutationPassed && freshCart.items.some(item => cartModelProduct(item)))
        throw new Error('Reîncărcați coșul după modificarea anterioară.');
      currentCart = freshCart;
      cartReferenceRevision++; unknownCartRows = '';
      updateCartReferences();
      const changes = mismatches(currentCart);
      if (!changes.length) return false;
      for (const {item, variant, properties} of changes) {
        if (String(variant.id) === String(item.variant_id || item.id)) {
          await cartRequest('cart/change.js', {id: item.key, quantity: item.quantity, properties});
          continue;
        }
        const added = await cartRequest('cart/add.js', {items: [{
          id: variant.id, quantity: item.quantity, properties
        }]});
        try {
          await cartRequest('cart/change.js', {id: item.key, quantity: 0});
        } catch (error) {
          // Do not leave an extra bed behind if removing the old line fails.
          const created = added.items?.[0];
          if (created?.key) await cartRequest('cart/change.js', {
            id: created.key, quantity: Math.max(0, created.quantity - item.quantity)
          });
          throw error;
        }
      }
      if (changes.some(({item, variant}) => String(variant.id) !== String(item.variant_id || item.id))) {
        const after = await cartRequest('cart.js');
        if (!Array.isArray(after?.items) || !after.items.every(validCartItem) || inconsistentBedQuantity(after) ||
            new Set(after.items.map(item => item.key)).size !== after.items.length)
          throw new Error('Cantitatea produsului necesită verificare și reconfigurare.');
        const byKey = new Map(changes.map(change => [change.item.key, change]));
        const unitState = (item, change) => JSON.stringify([
          String(item.product_id), String(change?.variant.id || item.variant_id || item.id),
          (change ? change.properties : item.properties) == null ?
            [Object.hasOwn(item, 'properties'), item.properties] :
            Object.entries(change ? change.properties : item.properties).sort(),
          ['price', 'original_price', 'final_price', 'discounted_price', 'total_discount', 'discounts',
            'line_level_discount_allocations'].map(field => item[field])
        ]);
        const groups = (items, expected = false) => {
          const totals = new Map();
          for (const item of items) {
            const state = unitState(item, expected ? byKey.get(item.key) : undefined);
            totals.set(state, (totals.get(state) || 0) + item.quantity);
          }
          return JSON.stringify([...totals].sort(([a], [b]) => a.localeCompare(b)));
        };
        const changedStates = new Set(changes.map(change => unitState(change.item, change)));
        const untouched = freshCart.items.filter(item => !byKey.has(item.key) && !changedStates.has(unitState(item)));
        if (groups(freshCart.items, true) !== groups(after.items) ||
            untouched.some(item => removalLineState(item) !==
              removalLineState(after.items.find(other => other.key === item.key) || {})))
          throw new Error('Coșul s-a modificat neașteptat. Verificați cantitățile și reconfigurați produsul.');
        currentCart = after; cartReferenceRevision++; unknownCartRows = '';
      }
      location.reload();
      return true;
    })();
    try { return await reconciling; }
    catch (error) {
      reconciliationFailed = true;
      let warning = document.getElementById('italflex-cart-configuration-error');
      if (!warning) {
        warning = document.createElement('p');
        warning.id = 'italflex-cart-configuration-error'; warning.setAttribute('role', 'alert');
        document.querySelector('[name="checkout"], #checkout')?.before(warning);
      }
      warning.textContent = 'Configurația sau cantitatea nu au putut fi confirmate. Reîncărcați coșul, verificați cantitatea și reconfigurați produsul înainte de finalizarea comenzii.';
      console.error('ItalFlex cart configuration:', error);
      return false;
    } finally { reconciling = undefined; }
  }
  function knownKeptCartBed(item) {
    if (!item || typeof item !== 'object') return false;
    const product = cartModelProduct(item);
    return product && String(item.product_id) === String(product.id) &&
      product.variants.some(variant => String(variant.id) === String(item.variant_id || item.id));
  }
  const hasKnownCartBed = cart => cartItems(cart).some(knownKeptCartBed);
  function hasKnownDOMCartBed() {
    return [...document.querySelectorAll('.cart-items__table-row[data-key]')].some(row => {
      const edit = row.querySelector('.edit_cart_option');
      if (!edit || row.dataset.parentKey || !row.closest?.('cart-items-component')) return false;
      const model = Object.values(data.unification?.models || {}).find(model =>
        String(model.productId) === String(edit.getAttribute('data-product_id')));
      const kept = Object.entries(model?.legacyVariants || {}).filter(([id, entry]) => id === String(entry.targetVariantId));
      return kept.length === 2 && new Set(kept.map(([, entry]) => entry.configuration)).size === 2 &&
        kept.every(([, entry]) => ['Fără saltea', 'Cu saltea'].includes(entry.configuration)) &&
        kept.some(([id]) => id === String(edit.getAttribute('data-variant_id')));
    });
  }
  let nativeMutationPassed = false;
  function bedRemovalScope(target, quantityEvent = false) {
    let button = target?.closest?.('button.cart-items__remove');
    const owner = quantityEvent ? target?.closest?.('cart-quantity-selector-component') : button;
    const row = owner?.closest('.cart-items__table-row[data-key]');
    button ||= row?.querySelector('button.cart-items__remove');
    if (!row || !button?.closest('cart-items-component')) return null;
    // An active known edit protects standard controls only; it never authorizes a cart write.
    if (!cartEditOpening && !hasKnownCartBed(currentCart) && !hasKnownCartBed(data.cart) && !hasKnownDOMCartBed()) return null;
    const edit = row.querySelector('.edit_cart_option');
    const observed = [...cartItems(currentCart), ...cartItems(data.cart)]
      .find(item => item?.key === row.dataset.key);
    const hint = edit && {variant_id: edit.getAttribute('data-variant_id'), product_id: edit.getAttribute('data-product_id')};
    const identity = observed || hint || {};
    const input = row.querySelector('cart-quantity-selector-component input[ref="quantityInput"]');
    return {button, row, key: row.dataset.key, parentKey: row.dataset.parentKey, hint,
      productId: identity.product_id, variantId: identity.variant_id || identity.id,
      constraints: input && {min: input.getAttribute('min'), max: input.getAttribute('max'),
        step: input.getAttribute('step'), disabled: input.disabled}};
  }
  function removalLineState(item) {
    // Compare every property (including the opaque CTA) and monetary/discount
    // fields, never identify a sibling by a shared native variant or position.
    return JSON.stringify({key: item.key, id: item.id, variant_id: item.variant_id, product_id: item.product_id,
      quantity: item.quantity, properties: Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
      prices: ['price', 'original_price', 'final_price', 'discounted_price', 'line_price', 'original_line_price',
        'final_line_price', 'total_discount', 'discounts', 'line_level_discount_allocations'].map(key => item[key])});
  }
  function busyCartRows(scope) {
    for (const row of new Set([scope.row, ...document.querySelectorAll('.cart-items__table-row[data-key]')])) {
      row.setAttribute('aria-busy', 'true');
      row.querySelectorAll('button, input').forEach(control => { control.disabled = true; });
    }
  }
  async function removeExactBed(scope, quantity) {
    if (removalRequest) return removalRequest;
    const pendingReconciliation = reconciling;
    removalRequest = (async () => {
      try {
        busyCartRows(scope);
        // A previous repair can replace the line key and reload the document.
        // Never continue a removal after such a write or a failed cart check.
        if (pendingReconciliation && await pendingReconciliation) return false;
        if (reconciliationFailed) throw new Error('Reîncărcați coșul înainte de a elimina produsul.');
        if (nativeMutationPassed) throw new Error('Reîncărcați coșul după modificarea anterioară.');
        const rules = scope.constraints;
        const min = Number(rules?.min), step = Number(rules?.step), max = rules?.max == null || rules.max === '' ? null : Number(rules.max);
        if (!Number.isSafeInteger(quantity) || quantity < 0 || (quantity > 0 && (!rules || rules.disabled ||
            !/^\d+$/.test(rules.min) || !/^[1-9]\d*$/.test(rules.step) || !Number.isSafeInteger(min) ||
            !Number.isSafeInteger(step) || (max !== null && (!/^\d+$/.test(rules.max) || !Number.isSafeInteger(max))) ||
            quantity < min || (max !== null && quantity > max) || (quantity - min) % step !== 0)))
          throw new Error('Cantitatea nu respectă limitele produsului.');
        if (cartEditPending || document.querySelector('.tingle-modal--visible'))
          throw new Error('Încheiați editarea configurației înainte de a elimina produsul.');
        const key = scope.key;
        let postReadBudget;
        const readStableCart = async () => {
          let invalidatedReads = 0;
          while (true) {
            if (postReadBudget === 0) throw new Error('Coșul nu s-a stabilizat după actualizare.');
            if (postReadBudget !== undefined) postReadBudget--;
            const revision = cartReferenceRevision, cart = await cartRequest('cart.js');
            if (revision === cartReferenceRevision) {
              if (!Array.isArray(cart?.items) || !cart.items.every(validCartItem))
                throw new Error('Răspuns de coș invalid.');
              return cart;
            }
            if (++invalidatedReads === 3) throw new Error('Coșul s-a modificat în timpul verificării.');
          }
        };
        const before = await readStableCart();
        const items = before?.items;
        const matches = Array.isArray(items) ? items.filter(item => item.key === key) : [];
        const item = matches[0];
        if (!item || !hasKnownCartBed(before) || !/^[1-9]\d*$/.test(String(item.product_id)) ||
            !/^[1-9]\d*$/.test(String(item.variant_id || item.id)) ||
            String(item.product_id) !== String(scope.productId) || String(item.variant_id || item.id) !== String(scope.variantId) ||
            (scope.hint && (String(scope.hint.product_id) !== String(item.product_id) || String(scope.hint.variant_id) !== String(item.variant_id || item.id))) ||
            (products.some(product => String(product.id) === String(item.product_id)) && !knownKeptCartBed(item)) ||
            typeof key !== 'string' || !/^\d+:[a-f0-9]{32}$/.test(key) || matches.length !== 1 ||
            !key.startsWith(String(item.variant_id || item.id) + ':') || scope.parentKey || item.parent_relationship?.parent ||
            (quantity === 0 ? item.instructions?.can_remove === false : item.instructions?.can_update_quantity === false) ||
            (Object.hasOwn(item.properties || {}, '_mws_qty') && item.properties._mws_qty !== String(item.quantity)) ||
            items.some(other => !other || typeof other.key !== 'string' || !other.key ||
              !Number.isSafeInteger(other.quantity) || other.quantity < 1 ||
              !Number.isFinite(other.original_price) || !Number.isFinite(other.final_price)) ||
            new Set(items.map(item => item.key)).size !== items.length) throw new Error('Rândul exact nu mai poate fi identificat.');
        const siblings = items.filter(other => other.key !== key), siblingKeys = new Set(siblings.map(other => other.key));
        if (cartEditPending || document.querySelector('.tingle-modal--visible'))
          throw new Error('Configurația este în curs de editare.');
        await cartRequest('cart/change.js', {id: key, quantity});
        const expectedProperties = {...item.properties};
        if (Object.hasOwn(expectedProperties, '_mws_qty')) expectedProperties._mws_qty = String(quantity);
        const sameProperties = candidate => JSON.stringify(Object.entries(candidate.properties || {}).sort()) ===
          JSON.stringify(Object.entries(expectedProperties).sort());
        const unitFields = ['price', 'original_price', 'final_price', 'discounted_price', 'total_discount', 'discounts', 'line_level_discount_allocations'];
        let after;
        postReadBudget = 12;
        while (true) {
          after = await readStableCart();
          const remaining = cartItems(after).filter(other => !siblingKeys.has(other.key));
          const updated = remaining[0];
          const commonUnchanged = new Set(after.items.map(other => other.key)).size === after.items.length &&
            JSON.stringify(after.items.filter(other => siblingKeys.has(other.key)).map(removalLineState).sort()) ===
              JSON.stringify(siblings.map(removalLineState).sort());
          const targetUnchanged = remaining.length === 1 && updated.quantity === quantity &&
            updated.id === item.id && updated.variant_id === item.variant_id && updated.product_id === item.product_id &&
            new RegExp('^' + String(item.variant_id || item.id) + ':[a-f0-9]{32}$').test(updated.key) &&
            JSON.stringify(unitFields.map(field => updated[field])) === JSON.stringify(unitFields.map(field => item[field]));
          if (commonUnchanged && (quantity === 0 ? remaining.length === 0 : targetUnchanged &&
              (!Object.hasOwn(expectedProperties, '_mws_qty') || quantity === item.quantity || updated.key !== key) &&
              sameProperties(updated))) break;
          // The live app can update quantity before its existing private qty/key.
          // Wait only for that exact observed state, never another cart change.
          const privateQuantityPending = commonUnchanged && targetUnchanged && quantity > 0 && quantity !== item.quantity &&
            Object.hasOwn(item.properties || {}, '_mws_qty') && updated.key === key &&
            updated.properties?._mws_qty === item.properties._mws_qty &&
            JSON.stringify(Object.entries(updated.properties || {}).filter(([field]) => field !== '_mws_qty').sort()) ===
              JSON.stringify(Object.entries(expectedProperties).filter(([field]) => field !== '_mws_qty').sort());
          if (!privateQuantityPending || postReadBudget === 0)
            throw new Error('Coșul s-a modificat neașteptat după eliminare.');
          await new Promise(resolve => setTimeout(resolve, 250));
        }
        currentCart = after; cartReferenceRevision++; unknownCartRows = '';
        location.reload();
        return true;
      } catch (error) {
        reconciliationFailed = true;
        let warning = document.getElementById('italflex-cart-configuration-error');
        if (!warning) {
          warning = document.createElement('p'); warning.id = 'italflex-cart-configuration-error';
          warning.setAttribute('role', 'alert');
          (document.querySelector('[name="checkout"], #checkout') || scope.button).before(warning);
        }
        warning.textContent = 'Coșul nu a putut fi actualizat în siguranță. Reîncărcați coșul înainte de a continua.';
        console.error('ItalFlex cart removal:', error);
        return false;
      }
    })();
    try { return await removalRequest; } finally { removalRequest = undefined; }
  }
  function guardBedRemoval(event) {
    if (event.type === 'keydown' && !['Enter', ' ', 'Spacebar'].includes(event.key)) return;
    const scope = bedRemovalScope(event.target);
    if (!scope) {
      if (event.target?.closest?.('button.cart-items__remove')?.closest('cart-items-component')) nativeMutationPassed = true;
      return;
    }
    event.preventDefault(); event.stopImmediatePropagation();
    if (cartEditOpening) return;
    if (event.type !== 'dblclick') return removeExactBed(scope, 0);
  }
  function guardBusyQuantityControl(event) {
    if ((!removalRequest && !cartEditOpening) || (event.type === 'keydown' && ['Tab', 'Escape'].includes(event.key)) ||
        !event.target?.closest?.('cart-quantity-selector-component') || !bedRemovalScope(event.target, true)) return;
    event.preventDefault(); event.stopImmediatePropagation();
  }
  window.addEventListener('click', guardBusyQuantityControl, true);
  window.addEventListener('keydown', guardBusyQuantityControl, true);
  window.addEventListener('click', guardBedRemoval, true);
  window.addEventListener('keydown', guardBedRemoval, true);
  window.addEventListener('dblclick', guardBedRemoval, true);
  window.addEventListener('quantity-selector:update', event => {
    const scope = bedRemovalScope(event.target, true);
    if (!scope) {
      if (event.target?.closest?.('cart-quantity-selector-component')) nativeMutationPassed = true;
      return;
    }
    event.preventDefault(); event.stopImmediatePropagation();
    if (cartEditOpening) return;
    // The native producer updates its button states after dispatch returns.
    // Reapply busy state in the microtask, before another user activation.
    Promise.resolve().then(() => busyCartRows(scope));
    if (removalRequest) return;
    return removeExactBed(scope, event.detail?.quantity);
  }, true);
  // The application's saved record can have an older quantity than the cart.
  // Open only the exact fresh line, then initialize its public modal input once.
  function editQuantityWarning(context, message) {
    context.failed = true;
    let warning = document.getElementById('italflex-cart-edit-quantity-error');
    if (!warning) {
      warning = document.createElement('p'); warning.id = 'italflex-cart-edit-quantity-error';
      warning.setAttribute('role', 'alert');
      (context.modal?.querySelector('button') || context.link).before(warning);
    }
    warning.textContent = message;
  }
  function editLinkUnchanged(context) {
    return context.link.isConnected && context.row.isConnected && context.link.getClientRects().length && context.row.dataset.key === context.key &&
      context.link.closest('.cart-items__table-row[data-key]') === context.row &&
      !context.row.dataset.parentKey &&
      context.link.getAttribute('data-mw-apo-configure-link') === context.productId &&
      context.link.getAttribute('data-mw-apo-variant-id') === context.variantId &&
      context.link.getAttribute('data-mw-apo-cart-id') === context.cartId &&
      [...document.querySelectorAll('.cart-items__table-row[data-key]')].filter(row => row.dataset.key === context.key).length === 1;
  }
  async function openExactCartEdit(event) {
    const link = event.target?.closest?.('a.mw-apo-configure-link[data-mw-apo-configure-link]');
    if (!link || link === cartEditReplay || !/\/cart\/?$/.test(location.pathname)) return;
    const row = link.closest('.cart-items__table-row[data-key]');
    if (!row?.closest('cart-items-component')) return;
    if (cartEditOpening) {
      event.preventDefault(); event.stopImmediatePropagation(); return;
    }
    if (link.hasAttribute('data-mw-apo-buy-again-link')) return;
    const productId = link.getAttribute('data-mw-apo-configure-link');
    const product = products.find(item => String(item.id) === productId);
    if (!isUnified(product)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    const context = {link, row, productId, key: row.dataset.key,
      variantId: link.getAttribute('data-mw-apo-variant-id'), cartId: link.getAttribute('data-mw-apo-cart-id')};
    cartEditOpening = context;
    try {
      if (reconciling || removalRequest || checkoutRequest || cartEditPending || nativeMutationPassed ||
          document.querySelector('.tingle-modal--visible')) throw new Error('Încheiați operația curentă și reîncărcați coșul.');
      let cart, revision, invalidatedReads = 0;
      while (true) {
        revision = cartReferenceRevision; cart = await cartRequest('cart.js');
        if (revision === cartReferenceRevision) break;
        if (++invalidatedReads === 3) throw new Error('Coșul s-a modificat în timpul verificării.');
      }
      if (!Array.isArray(cart?.items) || !cart.items.every(validCartItem) ||
          new Set(cart.items.map(item => item.key)).size !== cart.items.length)
        throw new Error('Răspuns de coș invalid.');
      const matches = cart.items.filter(item => item.key === context.key), item = matches[0];
      if (matches.length !== 1 || !knownKeptCartBed(item) || !editLinkUnchanged(context) ||
          !new RegExp('^' + context.variantId + ':[a-f0-9]{32}$').test(context.key) ||
          String(item.product_id) !== context.productId || String(item.variant_id || item.id) !== context.variantId ||
          item.parent_relationship?.parent || !positiveCartId(context.cartId) ||
          typeof item.properties?._mws_cart !== 'string' || item.properties._mws_cart !== context.cartId ||
          cart.items.filter(other => other.properties?._mws_cart === context.cartId).length !== 1)
        throw new Error('Rândul exact nu mai poate fi identificat.');
      context.quantity = item.quantity; context.revision = revision; context.waiting = true;
      cartEditReplay = link;
      try { link.click(); } finally { cartEditReplay = undefined; }
    } catch {
      editQuantityWarning(context, 'Cantitatea editării nu a putut fi confirmată. Închideți editarea și reîncărcați coșul înainte de a reconfigura produsul.');
    }
  }
  window.addEventListener('click', openExactCartEdit, true);
  document.addEventListener('mwApo::ready', event => {
    const context = cartEditOpening;
    if (!context?.waiting || context.seeded || context.failed || String(event.detail?.productId) !== context.productId) return;
    try {
      const modals = [...document.querySelectorAll('.mw-tingle .tingle-modal--visible')];
      const modal = modals[0]; context.modal = modal;
      const inputs = [...modal?.querySelectorAll('.mw-product-info__quantity input') || []], input = inputs[0];
      const block = typeof event.detail?.blockId === 'string' && event.detail.blockId ? document.getElementById(event.detail.blockId) : null;
      const min = input?.getAttribute('min'), max = input?.getAttribute('max'), step = input?.getAttribute('step');
      if (modals.length !== 1 || inputs.length !== 1 || !input.getClientRects().length || input.disabled || input.readOnly ||
          input.type !== 'number' || min !== '1' || (step !== null && step !== '1') ||
          (max !== null && (!/^[1-9]\d*$/.test(max) || !Number.isSafeInteger(Number(max)) || context.quantity > Number(max))) ||
          (block && !modal.contains(block)) || !editLinkUnchanged(context) || context.revision !== cartReferenceRevision)
        throw new Error('Contextul editării nu mai poate fi confirmat.');
      context.seeded = true; context.modalSeen = true;
      input.value = String(context.quantity);
      input.dispatchEvent(new Event('input', {bubbles: true}));
      input.dispatchEvent(new Event('change', {bubbles: true}));
    } catch {
      editQuantityWarning(context, 'Cantitatea editării nu a putut fi confirmată. Anulați editarea și reîncărcați coșul înainte de a reconfigura produsul.');
    }
  });
  function guardUnreadyCartEdit(event) {
    const context = cartEditOpening;
    if (!context) return;
    const button = event.target?.closest?.('button');
    if (event.type === 'click' && button?.closest('.mw-tingle') && button.textContent.trim() === 'Cancel' && !cartEditPending) {
      context.modalSeen = true; return;
    }
    const submit = event.type === 'submit' ? event.target?.closest?.('.mw-tingle') &&
      (!event.submitter || cartEditSubmit(event.submitter)) : cartEditSubmit(event.target);
    if ((event.type !== 'keydown' || ['Enter', ' ', 'Spacebar'].includes(event.key)) && submit &&
        (!context.seeded || context.failed || context.revision !== cartReferenceRevision)) {
      event.preventDefault(); event.stopImmediatePropagation();
      editQuantityWarning(context, 'Cantitatea editării nu a putut fi confirmată. Anulați editarea și reîncărcați coșul înainte de a reconfigura produsul.');
    }
  }
  window.addEventListener('click', guardUnreadyCartEdit, true);
  window.addEventListener('keydown', guardUnreadyCartEdit, true);
  document.addEventListener('submit', guardUnreadyCartEdit, true);
  document.addEventListener('input', event => {
    if (cartEditOpening && !cartEditOpening.seeded && event.isTrusted &&
        event.target?.closest?.('.mw-tingle .mw-product-info__quantity'))
      editQuantityWarning(cartEditOpening, 'Cantitatea a fost modificată înainte de încheierea verificării. Anulați editarea și redeschideți configurarea.');
  }, true);
  const optionName = option => normal(option.querySelector('.mw-label .mw-text--style-strong')?.textContent);
  const selected = option => option?.querySelector('input:checked')?.getAttribute('aria-label') ||
    option?.querySelector('select')?.selectedOptions?.[0]?.textContent;
  function syncRoot(root) {
    const options = [...root.querySelectorAll('.mw-option')];
    const mattress = selected(options.find(option => optionName(option) === 'Saltea'));
    if (!mattress) return;
    const form = root.closest('form[action*="/cart/add"]') || root.closest('form');
    const fields = form ? [...form.querySelectorAll('[name="id"]')] : [];
    let product = fields.map(field => byVariant.get(String(field.value))).find(Boolean);
    if (!product) {
      const owner = root.closest('[data-product-id]');
      product = products.find(item => String(item.id) === owner?.dataset.productId);
    }
    if (!product && location.pathname.startsWith('/products/'))
      product = products.find(item => String(item.id) === String(data.current));
    if (!product) return;
    if (restoreLinkedSize(root, product)) { schedule(); return; }
    const size = dimension(selected(options.find(option => optionName(option) === 'Dimensiune')));
    const variant = tariffVariant(product, mattress, size);
    if (!variant) return;
    updateProductReferences(product, size);
    rememberLinkedSize(product, size, variant);
    const targets = fields.length ? fields : [...document.querySelectorAll('form[action*="/cart/add"] [name="id"]')];
    targets.forEach(field => {
      if (!byVariant.has(String(field.value)) || String(field.value) === String(variant.id)) return;
      field.value = String(variant.id);
      field.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
  function syncAll() {
    document.querySelectorAll('.mw-apo-container').forEach(syncRoot);
    updateCartReferences();
    if (cartEditOpening) {
      if (document.querySelector('.tingle-modal--visible')) cartEditOpening.modalSeen = true;
      else if (cartEditOpening.modalSeen && !cartEditPending) cartEditOpening = undefined;
    }
    if (cartEditPending && !document.querySelector('.tingle-modal--visible') && !/\/cart\/?$/.test(location.pathname)) {
      cartEditPending = false;
      reconcileCart();
    }
  }
  let scheduled;
  function schedule() {
    if (scheduled) return;
    scheduled = setTimeout(() => { scheduled = undefined; syncAll(); }, 20);
  }
  document.addEventListener('change', () => { syncAll(); schedule(); }, true);
  document.addEventListener('cart:update', cartResourceChanged);
  document.addEventListener('discount:update', cartResourceChanged);
  // Horizon 4.3 emits before its cart request completes. Its normalized cart
  // is not the AJAX resource this guard validates; reread only after settling.
  function standardCartResourceChanged(event) {
    const pending = event?.promise;
    if (!pending || typeof pending.then !== 'function') return;
    Promise.resolve(pending).then(
      () => cartResourceChanged({detail: {}}),
      error => {
        if (error?.name !== 'AbortError') console.warn('ItalFlex cart state:', error);
        cartResourceChanged({detail: {}});
      }
    );
  }
  document.addEventListener('shopify:cart:lines-update', standardCartResourceChanged);
  document.addEventListener('shopify:cart:discount-update', standardCartResourceChanged);
  const cartEditSubmit = target => {
    const button = target?.closest?.('button');
    return button?.closest('.mw-tingle') && button.textContent.trim() === 'Submit';
  };
  window.addEventListener('keydown', event => {
    if (['Enter', ' ', 'Spacebar'].includes(event.key) && cartEditSubmit(event.target)) {
      cartEditPending = true; schedule();
    }
  }, true);
  document.addEventListener('submit', event => {
    if (event.target?.closest?.('.mw-tingle') && (!event.submitter || cartEditSubmit(event.submitter))) {
      cartEditPending = true; schedule();
    }
  }, true);
  window.addEventListener('click', event => {
    const button = event.target.closest('button');
    if (button?.closest('.mw-tingle') && button.textContent.trim() === 'Submit') cartEditPending = true;
    if (event.target.closest('button[type="submit"], [name="add"], add-to-cart-component')) syncAll();
    schedule();
  }, true);
  let checkoutRequest;
  async function guardCheckout(event) {
    const control = (event.type === 'submit' ? event.submitter : event.target)
      ?.closest?.('[name="checkout"], #checkout');
    const implicitCartSubmit = event.type === 'submit' && !event.submitter && event.target?.id === 'cart-form';
    if (!control && !implicitCartSubmit) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (removalRequest) return removalRequest;
    if (checkoutRequest) return checkoutRequest;
    checkoutRequest = (async () => {
      try {
        if (!await reconcileCart() && !reconciliationFailed && !removalRequest) {
          // A cart form submitted without a button can be a quantity update.
          // Preserve its original POST instead of turning that into checkout.
          if (implicitCartSubmit) event.target.submit();
          else location.assign(cartRoot + 'checkout');
        }
      } catch (error) {
        // Another in-flight reconciliation can expose its underlying rejection.
        // Keep every checkout attempt blocked until the cart is reloaded.
        reconciliationFailed = true;
        console.error('ItalFlex checkout configuration:', error);
      }
    })();
    try { await checkoutRequest; } finally { checkoutRequest = undefined; }
  }
  document.addEventListener('click', guardCheckout, true);
  document.addEventListener('submit', guardCheckout, true);
  // Verify known beds against a fresh cart before repairing tariffs or Model.
  // Correct lines need no mutation, including after APO reloads or checkout.
  if (cartItems(data.cart).some(item => item && cartModelProduct(item)) || mismatches(data.cart).length || hasKnownDOMCartBed()) reconcileCart();
  document.addEventListener('submit', syncAll, true);
  new MutationObserver(schedule).observe(document.documentElement, {childList: true, subtree: true});
  syncAll();
})();
